import { useMemo, memo } from "react";
import { Platform, type StyleProp, type ViewStyle } from "react-native";
import { PressableScale } from "pressto";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { findNextTransitionOptimized } from "@src/features/alarms/utils/alarm-actions";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import { getCameraKeyString, getCameraDisplayName, isLegacyAppCamera } from "@src/utils/key-parser";
import type { CameraApiResponse } from "@src/features/cameras";
import { useCameraArmedStatus } from "@src/features/cameras/hooks/useCameraArmedStatus";
import { Icon, IconNameType } from "@src/components/Icon";
import { capitalize } from "@src/utils/capitalize";
import type { Href } from "expo-router";
import { NavPressable } from "@src/components/NavPressable";
import { SignedSnapshot, SnapshotImage } from "@visioni/a3s-media/app";
import { LegacySnapshotImage } from "./LegacySnapshotImage";
import { isWeb } from "@src/utils/web-helpers";

export interface CameraListItemProps {
  camera: CameraApiResponse;
  snapshot?: SignedSnapshot | null;
  siteId: string;
  onSelect: (cameraKey: string) => void;
  href?: Href;
  /** When true, applies selected state styling (e.g. for side panel context) */
  isSelected?: boolean;
  /** Optional style merged into the outer pressable container (e.g. flex:1 for grid cells). */
  style?: StyleProp<ViewStyle>;
}

export const CameraListItem = memo(
  ({ camera, siteId, onSelect, href, isSelected = false, snapshot, style }: CameraListItemProps) => {
    const { theme } = useUnistyles();
    const cameraKey = getCameraKeyString(camera);
    const { alarmDetail, isArmed } = useCameraArmedStatus({ camera, siteId });

    const isPaused = false; // TODO: API pending

    const scheduleTime = useMemo(() => {
      if (!alarmDetail) return null;
      const nextTransition = findNextTransitionOptimized({ alarmDetail });
      if (!nextTransition) return null;

      const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";
      const transitionDateTime = dateTimeRecordToLocalisedDateTime(nextTransition.datetimeRecord, timezone);

      const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const day = dayNames[transitionDateTime.weekday - 1];

      const hours = transitionDateTime.hour;
      const minutes = transitionDateTime.minute;
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes.toString().padStart(2, "0");

      const action = nextTransition.toState === "armed" ? "Arms" : "Disarms";
      return `${action} ${day} ${formattedHours}:${formattedMinutes} ${ampm}`;
    }, [alarmDetail]);

    const displayName = camera.name || getCameraDisplayName(cameraKey);

    const cameraIdDisplay = isLegacyAppCamera(camera) ? `Camera ID: ${camera.key.id}` : cameraKey;

    const thumbnailHeight = Math.ceil(theme.spacing["15xl"] * 1.465);

    const statusBadge = useMemo(() => {
      if (isPaused) {
        return {
          label: "Paused",
          iconName: "pause" as IconNameType,
          backgroundColor: theme.colors.grey[500],
          borderColor: theme.colors.grey[400],
        };
      }
      if (isArmed) {
        return {
          label: "Armed",
          iconName: "lockClosed" as IconNameType,
          backgroundColor: theme.colors.purple[600],
          borderColor: theme.colors.purple[300],
        };
      }
      return {
        label: "Disarmed",
        iconName: "lockOpen" as IconNameType,
        backgroundColor: theme.colors.warning[700],
        borderColor: theme.colors.warning[400],
      };
    }, [isPaused, isArmed, theme]);

    const content = (
      <>
        {/* Camera Thumbnail */}
        <View style={[styles.thumbnailContainer, { height: thumbnailHeight }]}>
          {snapshot ?
            <SnapshotImage
              snapshot={snapshot}
              width={400}
              style={styles.thumbnail}
              placeholderColor={theme.colors.borderCard}
            />
          : <LegacySnapshotImage
              cameraKey={cameraKey}
              thumbnailUrl={camera.snapshot?.url}
              capturedAt={camera.snapshot?.capturedAt}
            />
          }
        </View>

        {/* Camera Info */}
        <View p={isWeb ? "4xl" : "lg"} style={styles.containerCameraInfo}>
          <View>
            <View flex={1}>
              <Text textStyle="md/medium" numberOfLines={3} adjustsFontSizeToFit>
                {capitalize(displayName)}
              </Text>
              <Text textStyle="sm/regular" mt="xs" numberOfLines={3} style={styles.textSecondary}>
                {cameraIdDisplay}
              </Text>
            </View>
          </View>

          {/* Bottom block: anchored to the bottom of the info container so cards in a
              grid keep divider/badge/schedule visually aligned regardless of how many
              lines the name/ID wrap to. */}
          <View style={styles.bottomBlock}>
            <View borderWidth={1} borderColor="muted" my="3xl" />

            <View
              flexDirection={isWeb ? "row" : "column"}
              alignItems={isWeb ? "center" : "flex-start"}
              justifyContent={isWeb ? "space-between" : "flex-start"}
              gap="xs"
              style={styles.containerCameraStatus}
            >
              {/* Armed/Disarmed Status */}
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: statusBadge.backgroundColor, borderColor: statusBadge.borderColor },
                ]}
              >
                <Icon name={statusBadge.iconName} size={14} color={theme.colors.white.default} />
                <Text textStyle="xs/regular" style={styles.textBadge}>
                  {statusBadge.label}
                </Text>
              </View>
              {scheduleTime ?
                <View>
                  <Text textStyle="xs/regular" style={styles.textSecondary}>
                    {scheduleTime}
                  </Text>
                </View>
              : <View>
                  <Text textStyle="xs/regular" style={styles.textSecondary}>
                    No upcoming changes
                  </Text>
                </View>
              }
            </View>
          </View>
        </View>
      </>
    );
    if (href) {
      return (
        <NavPressable
          onPress={() => onSelect(cameraKey)}
          href={href}
          style={[
            styles.container,
            {
              backgroundColor: isSelected ? theme.colors.surfaceCardInner : theme.colors.surface,
              borderColor: isSelected ? theme.colors.borderCard : theme.colors.border,
            },
            style,
          ]}
        >
          {content}
        </NavPressable>
      );
    }

    return (
      <PressableScale
        onPress={() => onSelect(cameraKey)}
        style={[
          styles.container,
          {
            backgroundColor: isSelected ? theme.colors.surfaceCardInner : theme.colors.surface,
            borderColor: isSelected ? theme.colors.borderCard : theme.colors.border,
          },
          style,
        ]}
      >
        {content}
      </PressableScale>
    );
  },
  (prevProps, nextProps) => {
    const cameraKeyEqual =
      prevProps.camera.key.id === nextProps.camera.key.id && prevProps.camera.key.asset === nextProps.camera.key.asset;
    const snapshotEqual = prevProps.snapshot?.url === nextProps.snapshot?.url;
    const legacySnapshotEqual =
      prevProps.camera.snapshot?.url === nextProps.camera.snapshot?.url &&
      prevProps.camera.snapshot?.capturedAt === nextProps.camera.snapshot?.capturedAt;
    const siteIdEqual = prevProps.siteId === nextProps.siteId;
    // Compare alarmSettings to ensure re-render when alarm state changes
    const alarmSettingsEqual =
      JSON.stringify(prevProps.camera.alarmSettings) === JSON.stringify(nextProps.camera.alarmSettings);
    const isSelectedEqual = prevProps.isSelected === nextProps.isSelected;
    const hrefEqual = prevProps.href === nextProps.href;
    const styleEqual = prevProps.style === nextProps.style;

    return (
      cameraKeyEqual &&
      snapshotEqual &&
      legacySnapshotEqual &&
      siteIdEqual &&
      alarmSettingsEqual &&
      isSelectedEqual &&
      hrefEqual &&
      styleEqual
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.spacing.md,
    ...(Platform.OS === "web" ? {} : { ...theme.shadows.md }),
  },
  thumbnailContainer: {
    width: "100%",
    position: "relative",
    borderTopLeftRadius: theme.spacing.md,
    borderTopRightRadius: theme.spacing.md,
    overflow: "hidden",
    backgroundColor: theme.colors.borderCard,
  },
  thumbnail: {
    width: "100%",
    height: "100%",
  },
  thumbnailPlaceholder: {
    width: "100%",
    height: "100%",
  },
  containerCameraInfo: {
    flex: 1,
    borderBottomRightRadius: theme.spacing.md,
    borderBottomLeftRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  bottomBlock: {
    marginTop: "auto",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.xs,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.spacing["3xl"],
    borderWidth: 1,
  },
  containerCameraStatus: {
    overflow: "hidden",
  },
  textBadge: {
    color: theme.colors.white.default,
  },
  textSecondary: {
    color: theme.colors.surface1.foregroundSecondary,
  },
}));
